// QR Kiosk v2 — §7.3 full-screen swipeable photo carousel (view-ticket AND edit-ticket).
// CSS scroll-snap track (NO swiper/embla — zero new npm deps), dark immersive layer,
// mono "2 / 5" counter, "Späť" pill. View mode: read-only. Edit mode (onDelete present):
// a red "Odstrániť" pill → ConfirmSheet (Áno/Nie) → STAGED removal (persisted only when
// the report form is submitted). Radix Dialog (modal: Escape + focus trap + scroll lock);
// Round-6: PORTALED into the kiosk overlay root (escapes any animated ancestor — its
// fixed chevrons/pills/counter then anchor to the viewport). The dark rgba literals +
// white-on-dark fills follow the MidasImageViewer precedent — the only sanctioned hex here (§8).
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Glyph, GD } from "../shared/glyphs";
import { pillVisual, BackArrow } from "../shared/BackPill";
import { usePressed } from "../shared/usePressed";
import { useKioskOverlayRoot } from "../shared/kioskOverlayRoot";
import { ConfirmSheet } from "../shared/ConfirmSheet";

export interface PhotoCarouselProps {
  images: { src: string; name?: string }[]; // pre-filtered: content-truthy images only
  initialIndex: number;
  onClose: () => void;
  onDelete?: (index: number) => void; // present = edit mode (delete pill + confirm)
}

const DARK = "rgba(10,12,16,0.94)";
const WHITE_FILL = "rgba(255,255,255,0.14)";

export function PhotoCarousel({ images, initialIndex, onClose, onDelete }: PhotoCarouselProps) {
  const { translate } = useTranslation("public");
  const { isPhone } = useBreakpoint();
  const overlayRoot = useKioskOverlayRoot();
  const trackRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [current, setCurrent] = useState(initialIndex);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const backPress = usePressed();
  const delPress = usePressed();
  const editMode = !!onDelete;

  // Init scroll to the tapped image before paint (no animation).
  useLayoutEffect(() => {
    const t = trackRef.current;
    if (t) t.scrollLeft = initialIndex * t.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clamp when the array shrinks after a delete (empty is handled by confirmDelete → onClose).
  useEffect(() => {
    if (images.length === 0) return;
    if (current > images.length - 1) {
      const ni = images.length - 1;
      setCurrent(ni);
      const t = trackRef.current;
      if (t) t.scrollLeft = ni * t.clientWidth;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);

  const onScroll = () => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const t = trackRef.current;
      if (!t || t.clientWidth === 0) return;
      const idx = Math.round(t.scrollLeft / t.clientWidth);
      setCurrent((c) => (c === idx ? c : idx));
    });
  };

  const scrollByPage = (dir: 1 | -1) => {
    const t = trackRef.current;
    if (t) t.scrollBy({ left: dir * t.clientWidth, behavior: "smooth" });
  };

  const confirmDelete = () => {
    const wasLast = images.length <= 1;
    onDelete?.(current);
    setConfirmOpen(false);
    if (wasLast) onClose();
  };

  const chevBtn: React.CSSProperties = {
    position: "fixed",
    top: "50%",
    transform: "translateY(-50%)",
    width: 44,
    height: 44,
    borderRadius: 999,
    border: "none",
    background: WHITE_FILL,
    color: "#FFF",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    zIndex: 1405,
  };

  const currentName = images[current]?.name;

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal container={overlayRoot}>
      <Dialog.Content
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{ position: "fixed", inset: 0, zIndex: 1400, background: DARK }}
      >
        <Dialog.Title asChild>
          <span
            style={{ position: "absolute", width: 1, height: 1, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}
          >
            {`${current + 1} / ${images.length}`}
          </span>
        </Dialog.Title>

        {/* Swipe track — CSS scroll-snap (classes in kiosk-v2-tokens.css §5.2). */}
        <div className="kv2-carousel-track" ref={trackRef} onScroll={onScroll}>
          {images.map((img, i) => (
            <div className="kv2-carousel-slide" key={i}>
              <img
                src={img.src}
                alt={img.name || ""}
                draggable={false}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8, userSelect: "none" }}
              />
            </div>
          ))}
        </div>

        {/* Counter chip — top center; the swipe affordance signal ("there are more"). */}
        <div
          style={{
            position: "fixed",
            top: "calc(12px + env(safe-area-inset-top, 0px))",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1405,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12.5,
              fontWeight: 600,
              color: "#FFF",
              background: WHITE_FILL,
              padding: "5px 12px",
              borderRadius: 999,
            }}
          >
            {`${current + 1} / ${images.length}`}
          </span>
        </div>

        {/* ✕ close — top right (secondary exit; the pill is primary). */}
        <button
          type="button"
          aria-label={translate("kioskMidasClose")}
          onClick={onClose}
          style={{
            position: "fixed",
            top: "calc(12px + env(safe-area-inset-top, 0px))",
            right: 14,
            width: 44,
            height: 44,
            borderRadius: 999,
            border: "none",
            background: WHITE_FILL,
            color: "#FFF",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            zIndex: 1405,
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M6 6 L18 18" />
            <path d="M18 6 L6 18" />
          </svg>
        </button>

        {/* Tablet/desktop chevrons (phones swipe natively); hidden at the ends. */}
        {!isPhone && current > 0 && (
          <button type="button" aria-label="previous" onClick={() => scrollByPage(-1)} style={{ ...chevBtn, left: 14 }}>
            <span style={{ transform: "rotate(180deg)", display: "inline-flex" }}>
              <Glyph d={GD.chev} size={18} />
            </span>
          </button>
        )}
        {!isPhone && current < images.length - 1 && (
          <button type="button" aria-label="next" onClick={() => scrollByPage(1)} style={{ ...chevBtn, right: 14 }}>
            <Glyph d={GD.chev} size={18} />
          </button>
        )}

        {/* Bottom controls — centered row: Späť (view+edit) + Odstrániť (edit only). */}
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: "max(16px, calc(env(safe-area-inset-bottom, 0px) + 10px))",
            zIndex: 1405,
            display: "flex",
            justifyContent: "center",
            gap: 10,
            pointerEvents: "none",
          }}
        >
          <button
            type="button"
            {...backPress.pressHandlers}
            onClick={onClose}
            style={{ ...pillVisual, pointerEvents: "auto", ...backPress.pressStyle }}
          >
            <BackArrow />
            {translate("kioskBack")}
          </button>
          {editMode && (
            <button
              type="button"
              {...delPress.pressHandlers}
              onClick={() => setConfirmOpen(true)}
              style={{
                pointerEvents: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 48,
                padding: "0 22px",
                borderRadius: 999,
                border: "1px solid color-mix(in oklab, var(--red) 55%, var(--border))",
                background: "color-mix(in oklab, var(--red) 82%, var(--ink))",
                color: "#FFF",
                fontFamily: "inherit",
                fontSize: 14.5,
                fontWeight: 700,
                whiteSpace: "nowrap",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                ...delPress.pressStyle,
              }}
            >
              <Glyph d={GD.trash} size={18} />
              {translate("kioskV2DeletePhoto")}
            </button>
          )}
        </div>

        {editMode && (
          <ConfirmSheet
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title={translate("kioskV2DeletePhotoConfirmTitle")}
            message={
              <>
                {translate("kioskV2DeletePhotoConfirmHint")}
                {currentName && (
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                    {currentName}
                  </div>
                )}
              </>
            }
            onConfirm={confirmDelete}
            danger
            overlayZIndex={1410}
            contentZIndex={1411}
          />
        )}
      </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default PhotoCarousel;
